// frontend/src/components/pages/admin/organizations/AllOrgsTab.jsx
import { Building2, Search, X } from "lucide-react";
import ToggleSwitch from "../../../ui/ToggleSwitch";
import React, { useState, useMemo } from "react";

/**
 * OrganizationTable component to display organizations in a table
 * @param {Object} props - Component props
 * @param {Array} props.organizations - List of organizations to display
 * @param {Function} props.onOrgClick - Function to handle organization row click
 * @param {Function} props.onStatusChange - Function to handle organization status change
 * @param {boolean} [props.loading] - Loading state
 * @param {string} [props.tab] - Current tab (for future use)
 * @param {string} [props.searchQuery]
 * @param {Function} [props.setSearchQuery]
 * @param {string} [props.sortOption]
 * @param {Function} [props.setSortOption]
 * @param {Function} [props.onAddOrganization]
 * @param {Function} [props.setTab]
 * @returns {JSX.Element} Rendered OrganizationTable component
 */

// Skeleton loader for All Organizations tab
const TableSkeleton = () => (
  <div className="w-full flex justify-center">
    <div className="max-w-7xl w-full">
      {/* Search and Sort UI Skeleton */}
      <div className="flex w-full items-end gap-3 mb-6">
        {/* Search input skeleton */}
        <div className="flex-1 min-w-[200px]">
          <div className="h-4 w-40 bg-bg-40 rounded mb-1 animate-pulse" />
          <div className="h-9 w-full bg-bg-40 rounded animate-pulse" />
        </div>
        {/* Sort dropdown skeleton */}
        <div className="min-w-[150px]">
          <div className="h-4 w-16 bg-bg-40 rounded mb-1 animate-pulse" />
          <div className="h-9 w-full bg-bg-40 rounded animate-pulse" />
        </div>
        {/* Add button skeleton */}
        <div className="flex items-end">
          <div className="h-7 w-36 bg-bg-40 rounded animate-pulse" />
        </div>
      </div>

      {/* Table Skeleton */}
      <div className="bg-bg-60 rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-bd-50">
          <thead className="bg-bg-40">
            <tr>
              {[...Array(5)].map((_, i) => (
                <th key={i} className="px-6 py-3 text-left">
                  <div className="h-4 w-20 bg-bg-40 rounded animate-pulse"></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-bg-60 divide-y divide-bd-50">
            {[...Array(6)].map((_, i) => (
              <tr key={i}>
                {[...Array(5)].map((_, j) => (
                  <td key={j} className="px-6 py-4 whitespace-nowrap">
                    <div className="h-6 w-24 bg-bg-40 rounded animate-pulse"></div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const AllOrgsTab = ({
  organizations,
  onOrgClick,
  onStatusChange,
  onAddOrganization,
  loading = false,
}) => {
  // Local state for search and sort
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState("name-asc");

  // Filtered and sorted organizations
  const filteredOrganizations = useMemo(() => {
    let filtered = organizations.filter(
      (org) =>
        org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        org.domain.toLowerCase().includes(searchQuery.toLowerCase())
    );
    switch (sortOption) {
      case "name-asc":
        filtered = filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "name-desc":
        filtered = filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case "date-asc":
        filtered = filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case "date-desc":
        filtered = filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      default:
        break;
    }
    return filtered;
  }, [organizations, searchQuery, sortOption]);

  // Show skeleton if loading
  if (loading) {
    return <TableSkeleton />;
  }

  return (
    <div className="w-full flex justify-center">
      <div className="max-w-7xl w-full">
        {/* Search and Sort UI */}
        <div className="flex w-full items-end gap-3 mb-6">
          <div className="flex-1 min-w-[200px]">
            <label htmlFor="search" className="block text-xs font-medium text-fg-60 mb-1">
              Search Organizations
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-fg-60" />
              </div>
              <input
                id="search"
                type="text"
                className="w-full pl-10 pr-10 py-1.5 text-xs border border-bd-50 rounded-md bg-bg-60 text-fg-50 placeholder-fg-60 focus:outline-none focus:ring-1 focus:ring-ac-02 focus:border-ac-02"
                placeholder="Search by name or domain..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <X size={16} className="text-fg-60 hover:text-fg-50" />
                </button>
              )}
            </div>
          </div>
          <div>
            <label htmlFor="sort" className="block text-xs font-medium text-fg-60 mb-1">
              Sort By
            </label>
            <select
              id="sort"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="block w-full py-1.5 px-3 text-xs border border-bd-50 rounded-md bg-bg-60 text-fg-50 focus:outline-none focus:ring-1 focus:ring-ac-02 focus:border-ac-02"
            >
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="date-desc">Newest First</option>
              <option value="date-asc">Oldest First</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={onAddOrganization}
              className="flex items-center h-[28px] px-3 py-1.5 text-xs bg-ac-02 hover:bg-ac-01 rounded-md transition-colors shadow-sm text-white dark:text-black "
            >
              <span>Add Organization</span>
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-bg-60 rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-bd-50">
            <thead className="bg-bg-40">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider"
                >
                  Organization
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider"
                >
                  Domain
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider"
                >
                  Plan
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider"
                >
                  Created
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider"
                >
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-bg-60 divide-y divide-bd-50">
              {filteredOrganizations.length > 0 ? (
                filteredOrganizations.map((org) => (
                  <tr key={org.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-bg-40 flex items-center justify-center">
                          <Building2 size={20} className="text-fg-60" />
                        </div>
                        <div className="ml-4">
                          <div 
                            className="text-sm font-medium text-fg-50 hover:underline cursor-pointer"
                            onClick={() => onOrgClick(org)}
                          >
                            {org.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-fg-50">
                        {org.domain}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {(() => {
                        const planName = (org.planName || "Free").toLowerCase();
                        let badgeClass = "bg-gray-100 text-gray-600 dark:bg-gray-900/40 dark:text-gray-200";
                        if (planName === "free") badgeClass = "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/40 dark:text-yellow-200";
                        else if (planName === "pro") badgeClass = "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-200";
                        else if (planName === "enterprise") badgeClass = "bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-200";
                        return (
                          <span className={`px-2 py-1 rounded-md text-xs font-semibold ${badgeClass}`.trim()}>
                            {org.planName || "Free"}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-60">
                      {org.createdAt}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <ToggleSwitch 
                          checked={org.status === true || org.status === 'active'}
                          onChange={(isChecked) => onStatusChange(org, isChecked)}
                          label={org.status === true || org.status === 'active' ? 'Active' : 'Inactive'}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-4 text-center text-sm text-fg-60"
                  >
                    No organizations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AllOrgsTab;
